import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DoughnutChart from "../test/DoughnutChart";
import Chart from "chart.js/auto";
import "./profile.css";

// Add this import for animation
import { CSSTransition } from "react-transition-group";
import Loader from "../../../utils/loader/loader";
import ToastMessage from "../../../utils/toasts/toastMessage";
import {
  editProfile,
  profileCompletionPercentage,
  profileDetails,
} from "../../../Redux/profile/profile.action";
import { useNavigate } from "react-router-dom";
import { BOARD_OPTIONS } from "../../../utils/subjectBoard";
import { countryList } from "../../../utils/countryCode";

const ConcentricCircleChart = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const ctx = canvasRef.current.getContext("2d");
    const dataCC = {
      labels: ["A1", "A2", "A3", "A4", "A5", "A6", "A7", "A8"],
      datasets: [
        {
          data: [80, 100],
          backgroundColor: ["#73A4F4", "#F3F4F6"],
          borderWidth: 0,
        },
        {
          data: [150, 100],
          backgroundColor: ["transparent", "transparent"],
          borderWidth: 0,
          weight: 0.75,
        },
        {
          data: [150, 100],
          backgroundColor: ["#21CBAD", "#F3F4F6"],
          borderWidth: 0,
        },
        {
          data: [150, 100],
          backgroundColor: ["transparent", "transparent"],
          borderWidth: 0,
          weight: 0.75,
        },
        {
          data: [130, 100],
          backgroundColor: ["#91D1EA", "#F3F4F6"],
          borderWidth: 0,
        },
        {
          data: [150, 100],
          backgroundColor: ["transparent", "transparent"],
          borderWidth: 0,
          borderRadius: 0,
          weight: 0.75,
        },
        {
          data: [60, 100],
          backgroundColor: ["#F9969A", "#F3F4F6"],
          borderWidth: 0,
        },
        {
          data: [150, 100],
          backgroundColor: ["transparent", "transparent"],
          borderWidth: 0,
          weight: 0.75,
        },
      ],
    };

    const chartInstance = new Chart(ctx, {
      type: "doughnut",
      data: dataCC,
      options: {
        responsive: true,
        // borderRadius: 50,
        layout: {
          padding: {
            top: -10,
            left: 10,
            bottom: 0,
            right: 35,
          },
        },
        plugins: {
          tooltip: {
            enabled: true,
          },
          legend: {
            display: false,
          },
          datalabels: {
            display: false,
          },
        },
      },
      plugins: [
        {
          id: "outerLabels",
          afterDraw: (chart) => {
            const {
              ctx,
              chartArea: { width, height, left, top },
              data,
            } = chart;
            const labels = data.labels;
            ctx.save();
            ctx.font = "11px Inter";
            ctx.fillStyle = "#A79E9E";
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            const centerX = (width + left) / 2;
            const centerY = (height + top) / 2;
            const radius = (Math.min(width, height) / 2) * 0.938;

            labels.forEach((label, index) => {
              const angle = (index / labels.length) * 2 * Math.PI - Math.PI / 2;
              const x = centerX + (radius + 30) * Math.cos(angle);
              const y = centerY + (radius + 30) * Math.sin(angle);
              ctx.fillText(label, x, y);
            });
            ctx.restore();
          },
        },
      ],
    });

    return () => {
      chartInstance.destroy();
    };
  }, []);

  return <canvas ref={canvasRef} width={400} height={400} />;
};

const ProfileBody = ({ onProfileUpdateSuccess }) => {
  const dispatch = useDispatch();
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);
  const [toastState, setToastState] = useState({
    show: false,
    message: "",
    type: "",
  });

  const [missingFields, setMissingFields] = useState({});
  const [openBoardDropdown, setOpenBoardDropdown] = useState(false);
  const [openCountryDropdown, setOpenCountryDropdown] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");

  const store = useSelector((state) => state);

  const userDetails = useSelector(
    (state) => state?.ProfileManager?.user_details,
  );

  const [isEditable, setIsEditable] = useState(false);
  const [fields, setFields] = useState({
    class: userDetails?.grade || "",
    division: userDetails?.division || "",
    school: userDetails?.school || "",
    board: userDetails?.board || "",
    city: userDetails?.address || "",
    lastYear: userDetails?.last_percentage || null,
    country: userDetails?.country || "India",
    target: userDetails?.target_percentage || null,
    phone: userDetails?.phone_number || "",
    // Password: "................", // Added password to state
  });

  const [activeIndex, setActiveIndex] = useState(0);
  const [nextIndex, setNextIndex] = useState(0);
  const tabsRef = useRef(null);
  const selectorRef = useRef(null);
  const [showCloseBtn, setShowCloseBtn] = useState(false);

  const tabs = ["History Table 1", "History Table 2", "History Table 3"];

  const profileCompletion = useSelector(
    (state) => state?.ProfileManager?.completion?.profile_completion,
  );

  useEffect(() => {
    dispatch(profileDetails());
    dispatch(profileCompletionPercentage());
  }, [dispatch]);

  useEffect(() => {
    if (!profileCompletion) {
      setLoading(true);
    } else setLoading(false);
  }, [profileCompletion]);

  useEffect(() => {
    if (profileCompletion < 80) {
      setIsEditable(true);
    } else {
      setIsEditable(false);
    }
  }, [profileCompletion]);

  useEffect(() => {
    if (userDetails) {
      setFields({
        class: userDetails?.grade || "",
        division: userDetails?.division || "",
        school: userDetails?.school || "",
        board: userDetails?.board || "",
        city: userDetails?.address || "",
        lastYear: userDetails?.last_percentage || "",
        country: userDetails?.country || "",
        target: userDetails?.target_percentage || "",
        phone: userDetails?.phone_number || "",
        // password: "................", // Maintain password state on load
      });
    }
  }, [userDetails]);

  const filteredCountries = countryList?.filter((item) =>
  item.name.toLowerCase().includes(countrySearch.toLowerCase())
);

  const originalDataRef = useRef(null);

  const buildSnapshot = (f) =>
    JSON.stringify({
      school: f.school,
      board: f.board,
      city: f.city,
      country: f.country,
      lastYear: String(f.lastYear ?? ""),
      target: String(f.target ?? ""),
      phone: f.phone,
    });

  // Load user details into fields when switching to edit mode
  // const handleEdit = () => {
  //   setShowCloseBtn(true);
  //   setFields({
  //     class: userDetails?.grade || "",
  //     division: userDetails?.division || "",
  //     school: userDetails?.school || "",
  //     board: userDetails?.board || "",
  //     city: userDetails?.address || "",
  //     lastYear: userDetails?.last_percentage || null,
  //     country: userDetails?.country || "",
  //     target: userDetails?.target_percentage || null,
  //     phone: userDetails?.phone_number || "",
  //   });
  //   setIsEditable(true);
  // };

  const handleEdit = () => {
    setShowCloseBtn(true);
    const initialFields = {
      class: userDetails?.grade || "",
      division: userDetails?.division || "",
      school: userDetails?.school || "",
      board: userDetails?.board || "",
      city: userDetails?.address || "",
      lastYear: userDetails?.last_percentage || null,
      country: userDetails?.country || "",
      target: userDetails?.target_percentage || null,
      phone: userDetails?.phone_number || "",
    };
    setFields(initialFields);
    setIsEditable(true);
    originalDataRef.current = buildSnapshot(initialFields);
  };

  //  outside click close the dropdown content
  useEffect(() => {
    const handleClickOutside = (e) => {
      const dropdowns = [
        { className: "board-dropdown", setter: setOpenBoardDropdown },
        { className: "country-dropdown", setter: setOpenCountryDropdown },
      ];

      dropdowns.forEach(({ className, setter }) => {
        if (!e.target.closest(`.${className}`)) {
          setter(false);
        }
      });
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const handleCloseBtn = () => {
    setShowCloseBtn(false);
    setIsEditable(false);
  };

  // const handleInputChange = (e) => {
  //   setFields({
  //     ...fields,
  //     [e.target.name]: e.target.value,
  //   });
  //   if (missingFields[e.target.name])
  //     setMissingFields((prev) => ({ ...prev, [e.target.name]: false }));
  // };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let nextValue = value;

    if (name === "phone") {
      nextValue = value.replace(/\D/g, "").slice(0, 10);
    } else if (name === "lastYear" || name === "target") {
      // digits only, optional single decimal point, max 3 digits before it
      nextValue = value.replace(/[^0-9.]/g, "");
      const parts = nextValue.split(".");
      if (parts.length > 2)
        nextValue = parts[0] + "." + parts.slice(1).join("");
    }

    setFields({
      ...fields,
      [name]: nextValue,
    });
    if (missingFields[name])
      setMissingFields((prev) => ({ ...prev, [name]: false }));
  };

  const moveSelector = () => {
    const tabEls = tabsRef.current?.querySelectorAll("a");
    const targetTab = tabEls?.[nextIndex];
    const selector = selectorRef.current;

    if (targetTab && selector) {
      selector.style.left = `${targetTab.offsetLeft}px`;
      selector.style.width = `${targetTab.offsetWidth}px`;
      selector.style.top = `${targetTab.offsetTop}px`;
      selector.style.height = `${targetTab.offsetHeight}px`;
    }
  };

  useEffect(() => {
    moveSelector();

    const timer = setTimeout(() => {
      setActiveIndex(nextIndex);
    }, 300);

    window.addEventListener("resize", moveSelector);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", moveSelector);
    };
  }, [nextIndex, moveSelector]);

  const handleApply = async () => {
    setLoading(true);

    const requiredFields = [
      "class",
      "division",
      "school",
      "board",
      "city",
      "country",
      "lastYear",
      "target",
      "phone",
    ];

    const emptyFields = requiredFields.reduce((acc, field) => {
      if (!String(fields[field] ?? "").trim()) acc[field] = true;
      return acc;
    }, {});

    const lastYearNum = parseFloat(fields.lastYear);
    if (
      fields.lastYear !== "" &&
      (isNaN(lastYearNum) || lastYearNum < 0 || lastYearNum > 100)
    ) {
      setToastState({
        show: true,
        message: "Last Year's % must be a number between 0 and 100.",
        type: "error",
      });
      setMissingFields((prev) => ({ ...prev, lastYear: true }));
      setLoading(false);
      return;
    }

    const targetNum = parseFloat(fields.target);
    if (
      fields.target !== "" &&
      (isNaN(targetNum) || targetNum < 0 || targetNum > 100)
    ) {
      setToastState({
        show: true,
        message: "This Year's Target % must be a number between 0 and 100.",
        type: "error",
      });
      setMissingFields((prev) => ({ ...prev, target: true }));
      setLoading(false);
      return;
    }

    if (fields.phone && !/^[6-9]\d{9}$/.test(String(fields.phone).trim())) {
      setToastState({
        show: true,
        message: "Please enter a valid 10-digit Indian phone number.",
        type: "error",
      });
      setMissingFields((prev) => ({ ...prev, phone: true }));
      setLoading(false);
      return;
    }

    if (Object.keys(emptyFields).length > 0) {
      const missingFieldNames = Object.keys(emptyFields)
        .map((field) => field.replace("_", " "))
        .join(", ");

      setToastState({
        show: true,
        message: `Please fill: ${missingFieldNames}`,
        type: "error",
      });

      setMissingFields(emptyFields);
      setLoading(false);
      return;
    }

    if (fields.phone && !/^[6-9]\d{9}$/.test(fields.phone.trim())) {
      setToastState({
        show: true,
        message: "Please enter a valid 10-digit Indian phone number.",
        type: "error",
      });
      setMissingFields((prev) => ({ ...prev, phone: true }));
      setLoading(false);
      return;
    }

    const currentSnapshot = buildSnapshot(fields);
    if (
      originalDataRef.current &&
      currentSnapshot === originalDataRef.current
    ) {
      setToastState({
        show: true,
        message: "No changes detected. Nothing to update.",
        type: "error",
      });
      setLoading(false);
      setIsEditable(false);
      setShowCloseBtn(false);
      return;
    }

    setMissingFields({}); // clear if all filled

    const payload = {
      grade: fields.class,
      division: fields.division,
      school: fields.school,
      board: fields.board,
      address: fields.city,
      country: fields.country,
      target_percentage: +fields.target,
      last_percentage: +fields.lastYear,
      phone_number: fields.phone,
    };

    const result = await dispatch(editProfile(payload));

    if (result.success) {
      setToastState({
        show: true,
        message: "Profile updated successfully.",
        type: "success",
      });
      onProfileUpdateSuccess();
      dispatch(profileCompletionPercentage());
      dispatch(profileDetails());
      setIsEditable(false);
      setShowCloseBtn(false);
      originalDataRef.current = currentSnapshot;
    } else {
      setToastState({
        show: true,
        message: result.message || "Something went wrong! Try again.",
        type: "error",
      });
    }

    setLoading(false);
  };

  return (
    <div className="profile">
      <div className="profile__info">
        <div className="info-left">
          <div className="bdrs40 blue-block">
            <div className="w-100 title-bar">
              <div className="title-bar__left">
                <img
                  src="./assets/images/inner-pages/icons/icon-nav-user.svg"
                  alt=""
                />
                <div className="name">
                  <h2 className="text-capitalize mb-1 fw700 fz36">
                    {userDetails?.first_name} {userDetails?.last_name}
                  </h2>
                  {/* <p className="m-0">Your Recent Works</p> */}
                </div>
              </div>

              {showCloseBtn ? (
                <button
                  className="btn btn-solid btn-solid--dark"
                  onClick={handleCloseBtn}
                >
                  <span className="fw500 fz16 text-nowrap">Close</span>
                </button>
              ) : (
                <button
                  className="btn btn-solid btn-solid--dark"
                  onClick={handleEdit}
                >
                  <span className="fw500 fz16 text-nowrap">
                    {isEditable ? "Complete Your Profile" : "Edit profile"}
                  </span>
                </button>
              )}
            </div>
            <hr className="sep" />
            <h4 className="lh1 fz24 fw700">Personal info</h4>
            <ul className="list-inline more-info">
              <li>
                <div className="fz18 fw500 pair">
                  <div className="label">
                    Class/ Grades{" "}
                    <span className={isEditable ? "error-mandate" : ""}>*</span>
                  </div>
                  <input
                    type="text"
                    className="fz18 form-control"
                    disabled
                    name="class"
                    readOnly={!isEditable}
                    value={fields?.class}
                    onChange={handleInputChange}
                  />
                </div>
              </li>
              <li>
                <div className="fz18 fw500 pair">
                  <div className="label">
                    Division
                    <span className={isEditable ? "error-mandate" : ""}>*</span>
                  </div>
                  <input
                    type="text"
                    className="fz18 form-control"
                    name="division"
                    disabled
                    readOnly={!isEditable}
                    value={fields?.division}
                    onChange={handleInputChange}
                  />
                </div>
              </li>

              <li>
                <div className="fz18 fw500 pair">
                  <div className="label">
                    School/Institute{" "}
                    <span className={isEditable ? "error-mandate" : ""}>*</span>
                  </div>
                  <input
                    type="text"
                    className="fz18 form-control"
                    name="school"
                    readOnly={!isEditable}
                    value={fields?.school}
                    maxLength={50}
                    onChange={handleInputChange}
                  />
                </div>
              </li>
              <li>
                <div
                  className="fz18 fw500 pair subject-dropdown"
                  style={{ position: "relative" }}
                >
                  <div className="label">
                    Select Board{" "}
                    <span className={isEditable ? "error-mandate" : ""}>*</span>
                  </div>

                  <div
                    className={`fz18 form-control ${
                      missingFields.board ? "is-invalid" : ""
                    }`}
                    style={{
                      cursor: isEditable ? "pointer" : "",
                      userSelect: "none",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isEditable) setOpenBoardDropdown((prev) => !prev);
                    }}
                  >
                    {fields.board ? String(fields.board) : "Select Board"}
                    {isEditable && (
                      <span style={{ float: "right" }}>
                        {openBoardDropdown ? "▲" : "▼"}
                      </span>
                    )}
                  </div>

                  {openBoardDropdown && (
                    <div
                      style={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        right: 0,
                        background: "#fff",
                        border: "1px solid #ccc",
                        borderRadius: "6px",
                        zIndex: 10,
                        padding: "6px",
                        maxHeight: "200px",
                        overflowY: "auto",
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {BOARD_OPTIONS.map((item) => (
                        <label
                          key={item}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            cursor: isEditable ? "pointer" : "",
                            gap: "6px",
                            padding: "4px 0",
                          }}
                        >
                          <input
                            type="radio"
                            name="board"
                            checked={fields.board === item}
                            disabled={!isEditable}
                            onChange={() => {
                              handleInputChange({
                                target: { name: "board", value: item },
                              });
                              setOpenBoardDropdown(false); // close after selection
                            }}
                          />{" "}
                          {item}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </li>
              <li>
                <div className="fz18 fw500 pair">
                  <div className="label">
                    City
                    <span className={isEditable ? "error-mandate" : ""}>*</span>
                  </div>
                  <input
                    type="text"
                    className="fz18 form-control"
                    name="city"
                    readOnly={!isEditable}
                    value={fields?.city}
                    maxLength={50}
                    onChange={handleInputChange}
                  />
                </div>
              </li>
              <li>
                <div className="fz18 fw500 pair">
                  <div className="label">
                    Last Year's %{" "}
                    <span className={isEditable ? "error-mandate" : ""}>*</span>
                  </div>
                  <input
                    type="text"
                    className={`fz18 form-control ${missingFields.lastYear ? "is-invalid" : ""}`}
                    name="lastYear"
                    readOnly={!isEditable}
                    value={fields?.lastYear}
                    onChange={handleInputChange}
                    inputMode="decimal"
                  />
                </div>
              </li>

              <li>
                <div className="fz18 fw500 pair">
                  <div className="label">
                    This Year's Target %{" "}
                    <span className={isEditable ? "error-mandate" : ""}>*</span>
                  </div>
                  <input
                    type="text"
                    className={`fz18 form-control ${missingFields.target ? "is-invalid" : ""}`}
                    name="target"
                    readOnly={!isEditable}
                    value={fields?.target}
                    onChange={handleInputChange}
                    inputMode="decimal"
                  />
                </div>
              </li>

              <li>
                <div className="fz18 fw500 pair subject-dropdown">
                  <div className="label">
                    Country{" "}
                    <span className={isEditable ? "error-mandate" : ""}>*</span>
                  </div>

                  <div
                    className={`fz18 form-control dropdown-trigger ${
                      isEditable ? "is-editable" : ""
                    } ${missingFields.country ? "is-invalid" : ""}`}
                    onClick={(e) => {
                      e.stopPropagation();

                      if (isEditable) {
                        setOpenCountryDropdown((prev) => !prev);
                        setCountrySearch("");
                      }
                    }}
                  >
                    {fields.country ? String(fields.country) : "Select Country"}

                    {isEditable && (
                      <span className="dropdown-arrow">
                        {openCountryDropdown ? "▲" : "▼"}
                      </span>
                    )}
                  </div>

                  {openCountryDropdown && (
                    <div
                      className="dropdown-menu-custom"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {/* Search */}
                      <input
                        type="text"
                        className="country-search"
                        placeholder="Search country..."
                        value={countrySearch}
                        onChange={(e) => setCountrySearch(e.target.value)}
                        autoFocus
                      />

                      {/* Countries */}
                      {filteredCountries?.length > 0 ? (
                        filteredCountries.map((item) => (
                          <label
                            key={item.code}
                            className={isEditable ? "is-editable" : ""}
                          >
                            <input
                              type="radio"
                              name="country"
                              checked={fields.country === item.name}
                              disabled={!isEditable}
                              onChange={() => {
                                handleInputChange({
                                  target: {
                                    name: "country",
                                    value: item.name,
                                  },
                                });

                                setOpenCountryDropdown(false);
                                setCountrySearch("");
                              }}
                            />

                            {item.name}
                          </label>
                        ))
                      ) : (
                        <div className="no-country-found">No country found</div>
                      )}
                    </div>
                  )}
                </div>
              </li>
              <li>
                <div className="fz18 fw500 pair">
                  <div className="label">
                    Phone Number{" "}
                    <span className={isEditable ? "error-mandate" : ""}>*</span>
                  </div>
                  <input
                    type="text"
                    className={`fz18 form-control ${missingFields.phone ? "is-invalid" : ""}`}
                    name="phone"
                    readOnly={!isEditable}
                    value={fields?.phone}
                    onChange={handleInputChange}
                    maxLength={10}
                    inputMode="numeric"
                  />
                </div>
              </li>
              <li>
                <div className="fz18 fw500 pair">
                  <div className="label">Email</div>
                  <input
                    type="email"
                    required
                    disabled
                    className="fz18 form-control"
                    name="email"
                    value={userDetails?.email}
                  />
                </div>
              </li>
              {/* PASSWORD FIELD ADDED HERE */}
              {/* <li>
                <div className="fz18 fw500 pair">
                  <div className="label">Password</div>
                  <div style={{ position: "relative" }}>
                    <input
                      type="password"
                      className="fz18 form-control"
                      name="password"
                      value={fields?.password}
                      readOnly
                      disabled
                    />
                    {isEditable && (
                      <span
                        onClick={() => nav("/change-password")}
                        style={{
                          position: "absolute",
                          right: "10px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          cursor: "pointer",
                          color: "#0d6efd",
                          fontSize: "14px",
                          fontWeight: "600",
                        }}
                      >
                        Change
                      </span>
                    )}
                  </div>
                </div>
              </li> */}
            </ul>

            <CSSTransition
              in={isEditable}
              timeout={300}
              classNames="fade"
              unmountOnExit
            >
              <>
                <span className="fz18">
                  <span className="error-mandate">* </span> mandatory fields
                </span>
                <button
                  className="btn btn-solid btn-solid--dark btn-apply"
                  type="submit"
                  onClick={handleApply}
                >
                  Apply
                </button>
              </>
            </CSSTransition>
          </div>
          {/* <div className="graph-block">
            <h3 className="fz28 fw900">Overview</h3>
            <div className="chart-block hide-below-768">
              <div className="grad-block grad-block--dark">
                <div className="grad-block__chart">
                  <DoughnutChart
                    elementId="yellowDonut"
                    color="#fff"
                    dataValue={70}
                    graphBorder="64%"
                  />
                </div>
                <div className="text-white grad-block__text">
                  <div className="fw900 fz22">175</div>
                  <p className="m-0 fw500 lh1 fz16">Test Performance</p>
                </div>
              </div>
              <div className="grad-block grad-block--dark">
                <div className="grad-block__chart">
                  <DoughnutChart
                    elementId="cyanDonut"
                    color="#fff"
                    dataValue={60}
                    graphBorder="64%"
                  />
                </div>
                <div className="text-white grad-block__text">
                  <div className="fw900 fz22">20</div>
                  <p className="m-0 fw500 lh1 fz16">Code Practice</p>
                </div>
              </div>
              <div className="grad-block grad-block--dark">
                <div className="grad-block__chart">
                  <DoughnutChart
                    elementId="limegreenDonut"
                    color="#fff"
                    dataValue={30}
                    graphBorder="64%"
                  />
                </div>
                <div className="text-white grad-block__text">
                  <div className="fw900 fz22">152</div>
                  <p className="m-0 fw500 lh1 fz16">Total Performance</p>
                </div>
              </div>
            </div>
            <div className="m-carousel chart-block hide-above-768">
              <div className="owl-carousel owl-carousel--user owl-theme">
                <div className="item">
                  <div className="grad-block grad-block--purple">
                    <div className="grad-block__chart">
                      <canvas id="m-yellowDonut" />
                    </div>
                    <div className="text-white grad-block__text">
                      <div className="fw900 fz22">175</div>
                      <p className="m-0 fw500 lh1 fz16">Test Performance</p>
                    </div>
                  </div>
                </div>
                <div className="item">
                  <div className="grad-block grad-block--blue">
                    <div className="grad-block__chart">
                      <canvas id="m-cyanDonut" />
                    </div>
                    <div className="text-white grad-block__text">
                      <div className="fw900 fz22">20</div>
                      <p className="m-0 fw500 lh1 fz16">Code Practice</p>
                    </div>
                  </div>
                </div>
                <div className="item">
                  <div className="grad-block grad-block--orange">
                    <div className="grad-block__chart">
                      <canvas id="m-limegreenDonut" />
                    </div>
                    <div className="text-white grad-block__text">
                      <div className="fw900 fz22">152</div>
                      <p className="m-0 fw500 lh1 fz16">Total Performance</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div> */}
        </div>
        <div className="info-right">
          <div className="bdrs40 checklist-content">
            <div className="text-center">
              <h3 className="fw700 fz28 text-white  show-above-768 hide-below-768">
                Complete your profile
              </h3>
            </div>
            <div className="success-chart" style={{ zIndex: 1 }}>
              <DoughnutChart
                elementId="profileSuccessDonut"
                color="#32FB6F"
                dataValue={profileCompletion || 0}
                graphBorder="78%"
              />
              <div className="fw700 text-white fz44 inner-num">
                {profileCompletion || 0}%
              </div>
            </div>
            <ul className="list-inline checklist">
              <li>
                <div className="ps-1 fw500 fz20 checkbox-container">
                  <input
                    id="cklt-01"
                    type="checkbox"
                    name="cklt"
                    className="cklt-input cklt-toggle"
                    checked={
                      !!(userDetails?.grade && userDetails?.grade !== "NA")
                    }
                    readOnly
                  />
                  <label htmlFor="cklt-01">
                    <b />
                    <span className="ps-1">Class/ Grades:</span>
                  </label>
                </div>
              </li>
              <li>
                <div className="ps-1 fw500 fz20 checkbox-container">
                  <input
                    id="cklt-02"
                    type="checkbox"
                    name="cklt"
                    className="cklt-input cklt-toggle"
                    checked={
                      !!(userDetails?.school && userDetails?.school !== "NA")
                    }
                    readOnly
                  />
                  <label htmlFor="cklt-02">
                    <b />
                    <span className="ps-1">School/Institute</span>
                  </label>
                </div>
              </li>
              <li>
                <div className="ps-1 fw500 fz20 checkbox-container">
                  <input
                    id="cklt-03"
                    type="checkbox"
                    name="cklt"
                    className="cklt-input cklt-toggle"
                    checked={
                      !!(userDetails?.address && userDetails?.address !== "NA")
                    }
                    readOnly
                  />
                  <label htmlFor="cklt-03">
                    <b />
                    <span className="ps-1">City (State)</span>
                  </label>
                </div>
              </li>
              <li>
                <div className="ps-1 fw500 fz20 checkbox-container">
                  <input
                    id="cklt-04"
                    type="checkbox"
                    name="cklt"
                    className="cklt-input cklt-toggle"
                    checked={
                      !!(
                        userDetails?.last_percentage &&
                        userDetails?.last_percentage !== "NA"
                      )
                    }
                    readOnly
                  />
                  <label htmlFor="cklt-04">
                    <b />
                    <span className="ps-1">Last Year's %</span>
                  </label>
                </div>
              </li>
              <li>
                <div className="ps-1 fw500 fz20 checkbox-container">
                  <input
                    id="cklt-05"
                    type="checkbox"
                    name="cklt"
                    className="cklt-input cklt-toggle"
                    checked={
                      !!(userDetails?.board && userDetails?.board !== "NA")
                    }
                    readOnly
                  />
                  <label htmlFor="cklt-05">
                    <b />
                    <span className="ps-1">Board</span>
                  </label>
                </div>
              </li>
              <li>
                <div className="ps-1 fw500 fz20 checkbox-container">
                  <input
                    id="cklt-06"
                    type="checkbox"
                    name="cklt"
                    className="cklt-input cklt-toggle"
                    checked={
                      !!(
                        userDetails?.target_percentage &&
                        userDetails?.target_percentage !== "NA"
                      )
                    }
                    readOnly
                  />
                  <label htmlFor="cklt-06">
                    <b />
                    <span className="ps-1">This Year's Target %</span>
                  </label>
                </div>
              </li>
            </ul>
            <div className="cover-btn">
              <h3 className="fw700 fz24 text-white show-below-768 hide-above-768">
                Complete your profile
              </h3>
              <button
                type="button"
                className="btn fw500 fz18 btn-solid btn-solid--white"
              >
                {profileCompletion === 100 ? "Completed" : "Not completed"}
              </button>
            </div>
          </div>
          {/* <div class="bdrs40 successful-content">
            <img class="hide-below-768" src="./assets/images/inner-pages/profile-completed-illustration.svg" alt="" />
            <img class="hide-above-768" src="./assets/images/inner-pages/m-profile-completed-illustration.svg" alt="" />

            <div class="s-inner">
              <h2 class="text-white fw700 fz36">Congratulations!</h2>
              <p class="text-white mb-0 fz20">100% Profile completed</p>
            </div>
          </div> */}
        </div>
      </div>
      {/* <div className="profile__analyze">
        <div className="analyze-title">
          <h2 className="fw900 fz28">Analyze</h2>
          <p className="fz18 fw500">
          
          </p>
        </div>
        <div className="analyze-main">
          <div className="bdrs40 analyze-main__left">
            <div className="bdrs40 bg-inner">
              <div className="bg-inner__left">
                <div className="chart-text">
                  <span className="fz10 text-uppercase">Average level</span>
                  <div className="fw700 fz18 num">A4</div>
                </div>
                <ConcentricCircleChart />
              </div>
              <div className="bg-inner__right">
                <ul className="list-inline h-100">
                  <li>
                    <div className="col-pair">
                      <div className="color color--red" />
                      <div className="fz16 text">React</div>
                    </div>
                  </li>
                  <li>
                    <div className="col-pair">
                      <div className="color color--blue" />
                      <div className="fz16 text">Java</div>
                    </div>
                  </li>
                  <li>
                    <div className="col-pair">
                      <div className="color color--green" />
                      <div className="fz16 text">Python</div>
                    </div>
                  </li>
                  <li>
                    <div className="col-pair">
                      <div className="color color--darkBlue" />
                      <div className="fz16 text">PHP</div>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div className="bdrs40 fut-assignments analyze-main__right">
            <h2 className="text-white fw700 fz28 title">Future Assignment</h2>
            <div className="accordion" id="accordionExample">
              <div className="accordion-item">
                <h2 className="accordion-header">
                  <button
                    className="fz18 fw500 text-white accordion-button"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#collapseOne"
                    aria-expanded="true"
                    aria-controls="collapseOne"
                  >
                    Hindi
                  </button>
                </h2>
                <div
                  id="collapseOne"
                  className="accordion-collapse collapse show"
                  data-bs-parent="#accordionExample"
                >
                  <div className="pt-0 accordion-body">
                    <ul className="list-inline mb-0 pb-2 f-assign-list">
                      <li>
                        <div className="f-assign">
                          <div className="f-assign__title">
                            <div className="img-cover">
                              <img
                                src="./assets/images/inner-pages/icons/icon-fut-01.svg"
                                alt=""
                              />
                            </div>
                            <h3 className="fw700 fz20 text-white mb-0">
                              For some integer m, every even integer is of the
                              for voluptates minus ipsum corrupti voluptatem
                              itaque, temporibus quis perspiciatis cum?
                            </h3>
                          </div>
                        </div>
                      </li>
                      <li>
                        <div className="f-assign">
                          <div className="f-assign__title">
                            <div className="img-cover">
                              <img
                                src="./assets/images/inner-pages/icons/icon-fut-02.svg"
                                alt=""
                              />
                            </div>
                            <h3 className="fw700 fz20 text-white mb-0">
                              For some integer m, every even integer is of the
                              for voluptates minus ipsum corrupti voluptatem
                              itaque, temporibus quis perspiciatis cum?
                            </h3>
                          </div>
                        </div>
                      </li>
                      <li>
                        <div className="f-assign">
                          <div className="f-assign__title">
                            <div className="img-cover">
                              <img
                                src="./assets/images/inner-pages/icons/icon-fut-01.svg"
                                alt=""
                              />
                            </div>
                            <h3 className="fw700 fz20 text-white mb-0">
                              For some integer m, every even integer is of the
                              for voluptates minus ipsum corrupti voluptatem
                              itaque, temporibus quis perspiciatis cum?
                            </h3>
                          </div>
                        </div>
                      </li>
                      <li>
                        <div className="f-assign">
                          <div className="f-assign__title">
                            <div className="img-cover">
                              <img
                                src="./assets/images/inner-pages/icons/icon-fut-02.svg"
                                alt=""
                              />
                            </div>
                            <h3 className="fw700 fz20 text-white mb-0">
                              For some integer m, every even integer is of the
                              for voluptates minus ipsum corrupti voluptatem
                              itaque, temporibus quis perspiciatis cum?
                            </h3>
                          </div>
                        </div>
                      </li>
                      <li>
                        <div className="f-assign">
                          <div className="f-assign__title">
                            <div className="img-cover">
                              <img
                                src="./assets/images/inner-pages/icons/icon-fut-01.svg"
                                alt=""
                              />
                            </div>
                            <h3 className="fw700 fz20 text-white mb-0">
                              For some integer m, every even integer is of the
                              for voluptates minus ipsum corrupti voluptatem
                              itaque, temporibus quis perspiciatis cum?
                            </h3>
                          </div>
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="accordion-item">
                <h2 className="accordion-header">
                  <button
                    className="fz18 fw500 text-white accordion-button collapsed"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#collapseTwo"
                    aria-expanded="false"
                    aria-controls="collapseTwo"
                  >
                    Computer
                  </button>
                </h2>
                <div
                  id="collapseTwo"
                  className="accordion-collapse collapse"
                  data-bs-parent="#accordionExample"
                >
                  <div className="pt-0 accordion-body">
                    <ul className="list-inline mb-0 pb-2 f-assign-list">
                      <li>
                        <div className="f-assign">
                          <div className="f-assign__title">
                            <div className="img-cover">
                              <img
                                src="./assets/images/inner-pages/icons/icon-fut-01.svg"
                                alt=""
                              />
                            </div>
                            <h3 className="fw700 fz20 text-white mb-0">
                              For some integer m, every even integer is of the
                              for voluptates minus ipsum corrupti voluptatem
                              itaque, temporibus quis perspiciatis cum?
                            </h3>
                          </div>
                        </div>
                      </li>
                      <li>
                        <div className="f-assign">
                          <div className="f-assign__title">
                            <div className="img-cover">
                              <img
                                src="./assets/images/inner-pages/icons/icon-fut-02.svg"
                                alt=""
                              />
                            </div>
                            <h3 className="fw700 fz20 text-white mb-0">
                              For some integer m, every even integer is of the
                              for voluptates minus ipsum corrupti voluptatem
                              itaque, temporibus quis perspiciatis cum?
                            </h3>
                          </div>
                        </div>
                      </li>
                      <li>
                        <div className="f-assign">
                          <div className="f-assign__title">
                            <div className="img-cover">
                              <img
                                src="./assets/images/inner-pages/icons/icon-fut-01.svg"
                                alt=""
                              />
                            </div>
                            <h3 className="fw700 fz20 text-white mb-0">
                              For some integer m, every even integer is of the
                              for voluptates minus ipsum corrupti voluptatem
                              itaque, temporibus quis perspiciatis cum?
                            </h3>
                          </div>
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="accordion-item">
                <h2 className="accordion-header">
                  <button
                    className="fz18 fw500 text-white accordion-button collapsed"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#collapseThree"
                    aria-expanded="false"
                    aria-controls="collapseThree"
                  >
                    English
                  </button>
                </h2>
                <div
                  id="collapseThree"
                  className="accordion-collapse collapse"
                  data-bs-parent="#accordionExample"
                >
                  <div className="pt-0 accordion-body">
                    <ul className="list-inline mb-0 pb-2 f-assign-list">
                      <li>
                        <div className="f-assign">
                          <div className="f-assign__title">
                            <div className="img-cover">
                              <img
                                src="./assets/images/inner-pages/icons/icon-fut-01.svg"
                                alt=""
                              />
                            </div>
                            <h3 className="fw700 fz20 text-white mb-0">
                              For some integer m, every even integer is of the
                              for voluptates minus ipsum corrupti voluptatem
                              itaque, temporibus quis perspiciatis cum?
                            </h3>
                          </div>
                        </div>
                      </li>
                      <li>
                        <div className="f-assign">
                          <div className="f-assign__title">
                            <div className="img-cover">
                              <img
                                src="./assets/images/inner-pages/icons/icon-fut-02.svg"
                                alt=""
                              />
                            </div>
                            <h3 className="fw700 fz20 text-white mb-0">
                              For some integer m, every even integer is of the
                              for voluptates minus ipsum corrupti voluptatem
                              itaque, temporibus quis perspiciatis cum?
                            </h3>
                          </div>
                        </div>
                      </li>
                      <li>
                        <div className="f-assign">
                          <div className="f-assign__title">
                            <div className="img-cover">
                              <img
                                src="./assets/images/inner-pages/icons/icon-fut-01.svg"
                                alt=""
                              />
                            </div>
                            <h3 className="fw700 fz20 text-white mb-0">
                              For some integer m, every even integer is of the
                              for voluptates minus ipsum corrupti voluptatem
                              itaque, temporibus quis perspiciatis cum?
                            </h3>
                          </div>
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div> */}
      <div className="bdrs28 dashboard__history profile__history">
        {/* <div className="history-title">
          <h2 className="fw700 mb-0 fz28">History Table</h2>
          <a href="#" className="fz18 fw500 see-all">
            See All
          </a>
        </div> */}
        {/* <nav className="tabs">
          <div className="selector" />
          <a href="#" data-body="ht1" className="fz16 fw500 active-tab">
            History Table
          </a>
          <a href="#" data-body="ht2" className="fz16 fw500">
            History Table
          </a>
          <a href="#" data-body="ht3" className="fz16 fw500">
            History Table
          </a>
        </nav>
        <section className="tab-body">
          <div className="ht1">
            <ul className="w-100 list-unstyled h-table-list">
              <li>
                <button className="w-100 border-0 ts03 h-data select">
                  <div className="rounded-circle img-cover img-cover--purple">
                    <img
                      src="./assets/images/inner-pages/icons/icon-math.svg"
                      alt=""
                    />
                  </div>
                  <div className="text">
                    <h4 className="fz16 fw500">
                      At vero eos et accusamus et iusto odio dignissimos ducimus
                      qui blanditiis praesentium voluptatum deleniti atque
                      corrupti quos dolores et quas molestias excepturi sint
                      occaecati cupiditate non provident, similique sunt in
                      culpa qui officia deserunt mollitia animi, id est laborum
                      et dolorum fuga.
                    </h4>
                    <div className="fz10 fw500 time">02:20 AM, 03-Nov</div>
                  </div>
                </button>
              </li>
              <li>
                <button className="w-100 border-0 ts03 h-data">
                  <div className="rounded-circle img-cover img-cover--yellow">
                    <img
                      src="./assets/images/inner-pages/icons/icon-science.svg"
                      alt=""
                    />
                  </div>
                  <div className="text">
                    <h4 className="fz16 fw500">
                      At vero eos et accusamus et iusto odio dignissimos ducimus
                      qui blanditiis praesentium voluptatum deleniti atque
                      corrupti quos dolores et quas molestias excepturi sint
                      occaecati cupiditate non provident, similique sunt in
                      culpa qui officia deserunt mollitia animi, id est laborum
                      et dolorum fuga.
                    </h4>
                    <div className="fz10 fw500 time">02:20 AM, 03-Nov</div>
                  </div>
                </button>
              </li>
              <li>
                <button className="w-100 border-0 ts03 h-data">
                  <div className="rounded-circle img-cover img-cover--red">
                    <img
                      src="./assets/images/inner-pages/icons/icon-english.svg"
                      alt=""
                    />
                  </div>
                  <div className="text">
                    <h4 className="fz16 fw500">
                      At vero eos et accusamus et iusto odio dignissimos ducimus
                      qui blanditiis praesentium voluptatum deleniti atque
                      corrupti quos dolores et quas molestias excepturi sint
                      occaecati cupiditate non provident, similique sunt in
                      culpa qui officia deserunt mollitia animi, id est laborum
                      et dolorum fuga.
                    </h4>
                    <div className="fz10 fw500 time">02:20 AM, 03-Nov</div>
                  </div>
                </button>
              </li>
              <li>
                <button className="w-100 border-0 ts03 h-data">
                  <div className="rounded-circle img-cover img-cover--purple">
                    <img
                      src="./assets/images/inner-pages/icons/icon-math.svg"
                      alt=""
                    />
                  </div>
                  <div className="text">
                    <h4 className="fz16 fw500">
                      At vero eos et accusamus et iusto odio dignissimos ducimus
                      qui blanditiis praesentium voluptatum deleniti atque
                      corrupti quos dolores et quas molestias excepturi sint
                      occaecati cupiditate non provident, similique sunt in
                      culpa qui officia deserunt mollitia animi, id est laborum
                      et dolorum fuga.
                    </h4>
                    <div className="fz10 fw500 time">02:20 AM, 03-Nov</div>
                  </div>
                </button>
              </li>
            </ul>
          </div>
          <div className="ht2">
            <ul className="w-100 list-unstyled h-table-list">
              <li>
                <button className="w-100 border-0 ts03 h-data">
                  <div className="rounded-circle img-cover img-cover--yellow">
                    <img
                      src="./assets/images/inner-pages/icons/icon-science.svg"
                      alt=""
                    />
                  </div>
                  <div className="text">
                    <h4 className="fz16 fw500">
                      At vero eos et accusamus et iusto odio dignissimos ducimus
                      qui blanditiis praesentium voluptatum deleniti atque
                      corrupti quos dolores et quas molestias excepturi sint
                      occaecati cupiditate non provident, similique sunt in
                      culpa qui officia deserunt mollitia animi, id est laborum
                      et dolorum fuga.
                    </h4>
                    <div className="fz10 fw500 time">02:20 AM, 03-Nov</div>
                  </div>
                </button>
              </li>
              <li>
                <button className="w-100 border-0 ts03 h-data select">
                  <div className="rounded-circle img-cover img-cover--red">
                    <img
                      src="./assets/images/inner-pages/icons/icon-english.svg"
                      alt=""
                    />
                  </div>
                  <div className="text">
                    <h4 className="fz16 fw500">
                      At vero eos et accusamus et iusto odio dignissimos ducimus
                      qui blanditiis praesentium voluptatum deleniti atque
                      corrupti quos dolores et quas molestias excepturi sint
                      occaecati cupiditate non provident, similique sunt in
                      culpa qui officia deserunt mollitia animi, id est laborum
                      et dolorum fuga.
                    </h4>
                    <div className="fz10 fw500 time">02:20 AM, 03-Nov</div>
                  </div>
                </button>
              </li>
              <li>
                <button className="w-100 border-0 ts03 h-data">
                  <div className="rounded-circle img-cover img-cover--purple">
                    <img
                      src="./assets/images/inner-pages/icons/icon-math.svg"
                      alt=""
                    />
                  </div>
                  <div className="text">
                    <h4 className="fz16 fw500">
                      At vero eos et accusamus et iusto odio dignissimos ducimus
                      qui blanditiis praesentium voluptatum deleniti atque
                      corrupti quos dolores et quas molestias excepturi sint
                      occaecati cupiditate non provident, similique sunt in
                      culpa qui officia deserunt mollitia animi, id est laborum
                      et dolorum fuga.
                    </h4>
                    <div className="fz10 fw500 time">02:20 AM, 03-Nov</div>
                  </div>
                </button>
              </li>
              <li>
                <button className="w-100 border-0 ts03 h-data">
                  <div className="rounded-circle img-cover img-cover--yellow">
                    <img
                      src="./assets/images/inner-pages/icons/icon-science.svg"
                      alt=""
                    />
                  </div>
                  <div className="text">
                    <h4 className="fz16 fw500">
                      At vero eos et accusamus et iusto odio dignissimos ducimus
                      qui blanditiis praesentium voluptatum deleniti atque
                      corrupti quos dolores et quas molestias excepturi sint
                      occaecati cupiditate non provident, similique sunt in
                      culpa qui officia deserunt mollitia animi, id est laborum
                      et dolorum fuga.
                    </h4>
                    <div className="fz10 fw500 time">02:20 AM, 03-Nov</div>
                  </div>
                </button>
              </li>
            </ul>
          </div>
          <div className="ht3">
            <ul className="w-100 list-unstyled h-table-list">
              <li>
                <button className="w-100 border-0 ts03 h-data select">
                  <div className="rounded-circle img-cover img-cover--purple">
                    <img
                      src="./assets/images/inner-pages/icons/icon-math.svg"
                      alt=""
                    />
                  </div>
                  <div className="text">
                    <h4 className="fz16 fw500">
                      At vero eos et accusamus et iusto odio dignissimos ducimus
                      qui blanditiis praesentium voluptatum deleniti atque
                      corrupti quos dolores et quas molestias excepturi sint
                      occaecati cupiditate non provident, similique sunt in
                      culpa qui officia deserunt mollitia animi, id est laborum
                      et dolorum fuga.
                    </h4>
                    <div className="fz10 fw500 time">02:20 AM, 03-Nov</div>
                  </div>
                </button>
              </li>
              <li>
                <button className="w-100 border-0 ts03 h-data">
                  <div className="rounded-circle img-cover img-cover--yellow">
                    <img
                      src="./assets/images/inner-pages/icons/icon-science.svg"
                      alt=""
                    />
                  </div>
                  <div className="text">
                    <h4 className="fz16 fw500">
                      At vero eos et accusamus et iusto odio dignissimos ducimus
                      qui blanditiis praesentium voluptatum deleniti atque
                      corrupti quos dolores et quas molestias excepturi sint
                      occaecati cupiditate non provident, similique sunt in
                      culpa qui officia deserunt mollitia animi, id est laborum
                      et dolorum fuga.
                    </h4>
                    <div className="fz10 fw500 time">02:20 AM, 03-Nov</div>
                  </div>
                </button>
              </li>
              <li>
                <button className="w-100 border-0 ts03 h-data">
                  <div className="rounded-circle img-cover img-cover--red">
                    <img
                      src="./assets/images/inner-pages/icons/icon-english.svg"
                      alt=""
                    />
                  </div>
                  <div className="text">
                    <h4 className="fz16 fw500">
                      At vero eos et accusamus et iusto odio dignissimos ducimus
                      qui blanditiis praesentium voluptatum deleniti atque
                      corrupti quos dolores et quas molestias excepturi sint
                      occaecati cupiditate non provident, similique sunt in
                      culpa qui officia deserunt mollitia animi, id est laborum
                      et dolorum fuga.
                    </h4>
                    <div className="fz10 fw500 time">02:20 AM, 03-Nov</div>
                  </div>
                </button>
              </li>
              <li>
                <button className="w-100 border-0 ts03 h-data">
                  <div className="rounded-circle img-cover img-cover--yellow">
                    <img
                      src="./assets/images/inner-pages/icons/icon-science.svg"
                      alt=""
                    />
                  </div>
                  <div className="text">
                    <h4 className="fz16 fw500">
                      At vero eos et accusamus et iusto odio dignissimos ducimus
                      qui blanditiis praesentium voluptatum deleniti atque
                      corrupti quos dolores et quas molestias excepturi sint
                      occaecati cupiditate non provident, similique sunt in
                      culpa qui officia deserunt mollitia animi, id est laborum
                      et dolorum fuga.
                    </h4>
                    <div className="fz10 fw500 time">02:20 AM, 03-Nov</div>
                  </div>
                </button>
              </li>
            </ul>
          </div>
        </section> */}

        {/* <nav
          className="tabs relative d-flex gap-2 position-relative"
          ref={tabsRef}
        >
          <div className="selector absolute ts03" ref={selectorRef}></div>
          {tabs.map((tab, index) => (
            <a
              key={tab}
              href="#"
              className={`fz16 fw500   ${
                activeIndex === index ? "active-tab" : ""
              }`}
              onClick={(e) => {
                e.preventDefault();
                setNextIndex(index);
              }}
            >
              {tab}
            </a>
          ))}
        </nav> */}

        {/* <section className="tab-body">
          {tabs.map((tab, index) => (
            <div
              key={tab}
              style={{ display: activeIndex === index ? "block" : "none" }}
            >
              <ul className="w-100 list-unstyled h-table-list">
                <li>
                  <button className="w-100 border-0 ts03 h-data select">
                    <div className="rounded-circle img-cover img-cover--purple">
                      <img
                        src="./assets/images/inner-pages/icons/icon-math.svg"
                        alt=""
                      />
                    </div>
                    <div className="text">
                      <h4 className="fz16 fw500">
                        At vero eos et accusamus et iusto odio dignissimos
                        ducimus qui blanditiis praesentium voluptatum deleniti
                        atque corrupti quos dolores et quas molestias excepturi
                        sint occaecati cupiditate non provident, similique sunt
                        in culpa qui officia deserunt mollitia animi, id est
                        laborum et dolorum fuga.
                      </h4>
                      <div className="fz10 fw500 time">02:20 AM, 03-Nov</div>
                    </div>
                  </button>
                </li>
                <li>
                  <button className="w-100 border-0 ts03 h-data">
                    <div className="rounded-circle img-cover img-cover--yellow">
                      <img
                        src="./assets/images/inner-pages/icons/icon-science.svg"
                        alt=""
                      />
                    </div>
                    <div className="text">
                      <h4 className="fz16 fw500">
                        At vero eos et accusamus et iusto odio dignissimos
                        ducimus qui blanditiis praesentium voluptatum deleniti
                        atque corrupti quos dolores et quas molestias excepturi
                        sint occaecati cupiditate non provident, similique sunt
                        in culpa qui officia deserunt mollitia animi, id est
                        laborum et dolorum fuga.
                      </h4>
                      <div className="fz10 fw500 time">02:20 AM, 03-Nov</div>
                    </div>
                  </button>
                </li>
                <li>
                  <button className="w-100 border-0 ts03 h-data">
                    <div className="rounded-circle img-cover img-cover--red">
                      <img
                        src="./assets/images/inner-pages/icons/icon-english.svg"
                        alt=""
                      />
                    </div>
                    <div className="text">
                      <h4 className="fz16 fw500">
                        At vero eos et accusamus et iusto odio dignissimos
                        ducimus qui blanditiis praesentium voluptatum deleniti
                        atque corrupti quos dolores et quas molestias excepturi
                        sint occaecati cupiditate non provident, similique sunt
                        in culpa qui officia deserunt mollitia animi, id est
                        laborum et dolorum fuga.
                      </h4>
                      <div className="fz10 fw500 time">02:20 AM, 03-Nov</div>
                    </div>
                  </button>
                </li>
                <li>
                  <button className="w-100 border-0 ts03 h-data">
                    <div className="rounded-circle img-cover img-cover--yellow">
                      <img
                        src="./assets/images/inner-pages/icons/icon-science.svg"
                        alt=""
                      />
                    </div>
                    <div className="text">
                      <h4 className="fz16 fw500">
                        At vero eos et accusamus et iusto odio dignissimos
                        ducimus qui blanditiis praesentium voluptatum deleniti
                        atque corrupti quos dolores et quas molestias excepturi
                        sint occaecati cupiditate non provident, similique sunt
                        in culpa qui officia deserunt mollitia animi, id est
                        laborum et dolorum fuga.
                      </h4>
                      <div className="fz10 fw500 time">02:20 AM, 03-Nov</div>
                    </div>
                  </button>
                </li>
              </ul>
            </div>
          ))}
        </section> */}
        <Loader show={loading} />
        <ToastMessage
          show={toastState.show}
          onClose={() => setToastState({ show: false, message: "", type: "" })}
          message={toastState.message}
          type={toastState.type}
        />
      </div>
    </div>
  );
};

export default ProfileBody;
